import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Hash, Pencil, Trash2, Check, X, Search, Merge, Palette } from "lucide-react";
import { toast } from "sonner";
import { listKeywords, renameKeyword, deleteKeyword, setKeywordStyle } from "@/lib/api";
import { PALETTE, paletteToken } from "@/lib/palette";

export default function KeywordManagerDialog({ open, setOpen, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // {name, draft}
  const [colorPickerFor, setColorPickerFor] = useState(null); // keyword name whose palette is open

  const refresh = async () => {
    setLoading(true);
    try {
      setItems(await listKeywords());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? items.filter((k) => k.name.toLowerCase().includes(q)) : items;
  }, [items, search]);

  const existingNames = useMemo(
    () => new Set(items.map((k) => k.name.toLowerCase())),
    [items]
  );

  const commitRename = async () => {
    if (!editing) return;
    const from = editing.name;
    const to = editing.draft.trim();
    if (!to) return toast.error("Name required");
    if (to.toLowerCase() === from.toLowerCase()) {
      setEditing(null);
      return;
    }
    const isMerge = existingNames.has(to.toLowerCase());
    try {
      const res = await renameKeyword(from, to);
      toast.success(
        isMerge
          ? `Merged "${from}" into "${to}" (${res.updated} citation${res.updated === 1 ? "" : "s"})`
          : `Renamed "${from}" → "${to}"`
      );
      setEditing(null);
      await refresh();
      onChanged?.();
    } catch {
      toast.error("Failed to rename");
    }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Remove keyword "${name}" from every citation? This can't be undone.`)) return;
    try {
      const res = await deleteKeyword(name);
      toast.success(`Removed from ${res.updated} citation${res.updated === 1 ? "" : "s"}`);
      await refresh();
      onChanged?.();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleColor = async (name, color) => {
    try {
      await setKeywordStyle(name, color);
      setItems((prev) => prev.map((k) => (k.name === name ? { ...k, color } : k)));
      setColorPickerFor(null);
      onChanged?.();
    } catch {
      toast.error("Failed to save colour");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="keyword-manager-dialog"
        className="max-w-lg bg-[#FFF5FA] dark:bg-[#3B2350] border-[#F5D0E3] dark:border-[#45325A] max-h-[85vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#3B2350] dark:text-[#FBEFF6] flex items-center gap-2">
            <Hash className="w-5 h-5" /> Manage keywords
          </DialogTitle>
          <DialogDescription className="text-[#5E4B70] dark:text-[#BBA9CF]">
            Rename or delete keywords across every citation at once. Renaming to an existing keyword merges the two.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A6A8C]" />
          <Input
            data-testid="keyword-manager-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search keywords…"
            className="pl-9 bg-white dark:bg-[#1E1529] border-[#F5D0E3] dark:border-[#45325A]"
          />
        </div>

        <div className="overflow-y-auto flex-1 -mx-2 px-2">
          {loading ? (
            <div className="text-sm text-[#7A6A8C] py-6 text-center">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-[#7A6A8C] py-8 text-center">
              {items.length === 0 ? "No keywords yet — add some in a citation inspector." : "Nothing matches"}
            </div>
          ) : (
            <ul className="divide-y divide-[#F5D0E3] dark:divide-[#45325A]">
              {filtered.map((k) => {
                const isEditing = editing?.name === k.name;
                const willMerge =
                  isEditing &&
                  editing.draft.trim() &&
                  editing.draft.trim().toLowerCase() !== k.name.toLowerCase() &&
                  existingNames.has(editing.draft.trim().toLowerCase());
                return (
                  <li
                    key={k.name}
                    data-testid={`keyword-row-${k.name}`}
                    className="py-2.5 flex items-center gap-2"
                  >
                    {isEditing ? (
                      <>
                        <Input
                          data-testid="keyword-rename-input"
                          autoFocus
                          value={editing.draft}
                          onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitRename();
                            if (e.key === "Escape") setEditing(null);
                          }}
                          className="h-8 bg-white dark:bg-[#1E1529] border-[#F5D0E3] dark:border-[#45325A]"
                        />
                        {willMerge && (
                          <span className="text-xs uppercase tracking-wider text-[#C9286F] dark:text-[#C4A1FF] font-semibold flex items-center gap-1 shrink-0">
                            <Merge className="w-3 h-3" /> merge
                          </span>
                        )}
                        <button
                          data-testid="keyword-rename-save"
                          onClick={commitRename}
                          className="p-1.5 text-[#8B5CF6] dark:text-[#C4B5FD] hover:bg-[#EDE7FE] dark:hover:bg-[#2E2150] rounded"
                          aria-label="Save"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="p-1.5 text-[#7A6A8C] hover:text-[#3B2350] dark:hover:text-[#FBEFF6] rounded"
                          aria-label="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span
                          className="inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-full"
                          style={{ backgroundColor: paletteToken(k.color).bg, color: paletteToken(k.color).fg }}
                        >
                          <Hash className="w-3 h-3" />
                          {k.name}
                        </span>
                        <span className="text-xs text-[#7A6A8C]">
                          {k.count} {k.count === 1 ? "citation" : "citations"}
                        </span>
                        <div className="flex-1" />
                        <div className="relative">
                          <button
                            data-testid={`keyword-color-${k.name}`}
                            onClick={() => setColorPickerFor(colorPickerFor === k.name ? null : k.name)}
                            className="p-1.5 text-[#7A6A8C] hover:text-[#C9286F] dark:hover:text-[#C4A1FF] hover:bg-[#FCE7F3] dark:hover:bg-[#2E2140] rounded"
                            aria-label="Recolour"
                          >
                            <Palette className="w-3.5 h-3.5" />
                          </button>
                          {colorPickerFor === k.name && (
                            <div
                              data-testid={`keyword-color-picker-${k.name}`}
                              className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-[#3B2350] border border-[#F5D0E3] dark:border-[#45325A] rounded-lg shadow-lg p-2 flex gap-1.5"
                            >
                              {PALETTE.map((c) => (
                                <button
                                  key={c.id}
                                  onClick={() => handleColor(k.name, c.id)}
                                  data-testid={`keyword-color-option-${k.name}-${c.id}`}
                                  aria-label={c.label}
                                  title={c.label}
                                  className={`w-5 h-5 rounded-full border-2 hover:scale-110 transition-transform ${
                                    k.color === c.id ? "ring-2 ring-offset-1 ring-[#3B2350] dark:ring-[#FBEFF6]" : ""
                                  }`}
                                  style={{ backgroundColor: c.bg, borderColor: c.hex }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          data-testid={`keyword-edit-${k.name}`}
                          onClick={() => setEditing({ name: k.name, draft: k.name })}
                          className="p-1.5 text-[#7A6A8C] hover:text-[#C9286F] dark:hover:text-[#C4A1FF] hover:bg-[#FCE7F3] dark:hover:bg-[#2E2140] rounded"
                          aria-label="Rename"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`keyword-delete-${k.name}`}
                          onClick={() => handleDelete(k.name)}
                          className="p-1.5 text-[#7A6A8C] hover:text-[#C9286F] hover:bg-[#FCE4F0] dark:hover:bg-[#4A1D3A] rounded"
                          aria-label="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="text-xs text-[#7A6A8C] pt-2 border-t border-[#F5D0E3] dark:border-[#45325A]">
          Tip: rename to an existing keyword to <b>merge</b> them everywhere.
        </div>
      </DialogContent>
    </Dialog>
  );
}
